import { ErrorModal } from "@dexteel/mesf-core";
import { Box, MenuItem, Select, type SelectChangeEvent } from "@mui/material";
import type { ICellRendererParams } from "ag-grid-community";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import type { WorkOrder } from "@/shared/models/WorkOrder";
import { upsertWorkOrder } from "@/shared/repositories/WorkOrderRepository";
import { invalidateWorkOrderQueries } from "../hooks/invalidateWorkOrderQueries";
import { STATUS_COLORS, STATUS_OPTIONS } from "./statusConstants";

export const StatusDot = ({ color }: { color: string }) => (
	<Box
		component="span"
		sx={{
			width: 8,
			height: 8,
			borderRadius: "50%",
			backgroundColor: color,
			display: "inline-block",
			mr: 0.75,
			flexShrink: 0,
		}}
	/>
);

export const StatusCell = (params: ICellRendererParams) => {
	const wo = params.data as WorkOrder;
	const queryClient = useQueryClient();
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");

	const mutation = useMutation({
		mutationFn: (newStatus: string) =>
			upsertWorkOrder({ ...wo, Status: newStatus }),
		onMutate: () => setSaving(true),
		onSettled: () => setSaving(false),
		onSuccess: async (response) => {
			if (response.ok) {
				// Status changes can un-run/complete the order: bundles and gantts move too
				await invalidateWorkOrderQueries(queryClient);
			} else {
				setError(response.message);
			}
		},
		onError: (err: Error) => setError(err.message),
	});

	const [confirmComplete, setConfirmComplete] = useState(false);
	// Pending/Cancelled un-run the order: window cleared, bundles released
	const [confirmUnrun, setConfirmUnrun] = useState<
		"Pending" | "Cancelled" | null
	>(null);

	const handleChange = (e: SelectChangeEvent<string>) => {
		const newStatus = e.target.value;
		if (newStatus === wo.Status) return;
		if (newStatus === "Completed") {
			setConfirmComplete(true);
			return;
		}
		if (
			(newStatus === "Pending" || newStatus === "Cancelled") &&
			(wo.Status === "Running" || wo.Status === "Completed")
		) {
			setConfirmUnrun(newStatus);
			return;
		}
		mutation.mutate(newStatus);
	};

	const colors = STATUS_COLORS[wo.Status] || STATUS_COLORS.Pending;
	const statusOptions = STATUS_OPTIONS.filter((option) => {
		if (option === "Loaded" && wo.Status !== "Loaded") return false;
		if (
			wo.Status === "Loaded" &&
			(option === "Pending" || option === "Cancelled")
		) {
			return false;
		}
		return true;
	});

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				height: "100%",
				opacity: saving ? 0.5 : 1,
				transition: "opacity 0.2s",
			}}
		>
			<Select
				value={wo.Status}
				onChange={handleChange}
				variant="standard"
				disableUnderline
				size="small"
				disabled={saving}
				sx={{
					fontSize: "0.8125rem",
					fontWeight: 500,
					color: colors.color,
					"& .MuiSelect-select": {
						display: "flex",
						alignItems: "center",
						py: 0,
						pr: "20px !important",
					},
					"& .MuiSelect-icon": {
						color: colors.color,
						right: -2,
					},
				}}
				renderValue={(value) => {
					const c = STATUS_COLORS[value] || STATUS_COLORS.Pending;
					return (
						<>
							<StatusDot color={c.color} />
							{value}
						</>
					);
				}}
			>
				{statusOptions.map((option) => {
					const c = STATUS_COLORS[option] || STATUS_COLORS.Pending;
					return (
						<MenuItem
							key={option}
							value={option}
							sx={{
								fontSize: "0.8125rem",
								display: "flex",
								alignItems: "center",
								gap: 0.75,
							}}
						>
							<StatusDot color={c.color} />
							{option}
						</MenuItem>
					);
				})}
			</Select>
			<ConfirmationDialogRaw
				open={confirmComplete}
				title="Complete this work order?"
				message="This stamps the order's end at the current time. Use Edit Times afterwards if the real end was different. Continue?"
				onClose={(value) => {
					setConfirmComplete(false);
					if (value === "OK") mutation.mutate("Completed");
				}}
			/>
			<ConfirmationDialogRaw
				open={confirmUnrun !== null}
				title={
					confirmUnrun === "Cancelled"
						? "Cancel this work order?"
						: "Back to Pending?"
				}
				message="This marks the order as never run: its start/end are cleared and all its bundles are unlinked. Continue?"
				onClose={(value) => {
					const target = confirmUnrun;
					setConfirmUnrun(null);
					if (value === "OK" && target) mutation.mutate(target);
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
